/**
 * PURPOSE: Clasificación por universidad y medallero de Atletismo (solo lectura).
 * RESPONSIBILITIES: Mostrar `GET .../standings` tal cual: puesto, universidad, puntos por categoría
 *   (Noveles Damas, Noveles Varones, Avanzados Damas, Avanzados Varones, Mixtas), total, medallas y cantidad de 1.º a
 *   8.º puestos. Los puntos de las combinadas (Decatlón y Heptatlón) ya están dentro del total y de Avanzados; la API sigue
 *   mandando `points.combinadas` pero la pantalla no la muestra. El admin incluye resultados provisionales. Botón discreto
 *   "Descargar cómputo (PDF)" (lo genera el servidor; si no cuadra con la clasificación, muestra el error) y un ⓘ junto al
 *   título que abre "Pruebas que suman en la clasificación" con la lista del servidor (`pruebasContempladas`).
 * COLLABORATORS: AtletismoResultadosArea.jsx, hooks/useAthleticsResults.js, services/athleticsResultsService.js.
 * ROUTE: winscore/src/area/AtletismoResultadosArea/components/StandingsView.jsx
 */
import React, { useState } from "react";
import { toast } from "react-hot-toast";
import { FaFilePdf } from "react-icons/fa6";
import { FiInfo, FiRefreshCw } from "react-icons/fi";

import { useAthleticsStandings } from "../../../hooks/useAthleticsResults.js";
import { downloadAthleticsComputo, saveAthleticsFile } from "../../../services/athleticsResultsService.js";
import { parseAthleticsError } from "../../../utils/athleticsResultsErrors.js";
import { FrozenScroll, buildFrozenColumns } from "./grids/gridCells.jsx";
import PruebasQueSumanModal from "./PruebasQueSumanModal.jsx";
import { Badge, Card, EmptyState, ErrorState, LoadingState, Spinner, secondaryButtonClass } from "./ui.jsx";

const POINT_COLUMNS = [
  { key: "novelesDamas", label: "Noveles Damas" },
  { key: "novelesVarones", label: "Noveles Varones" },
  { key: "avanzadosDamas", label: "Avanzados Damas" },
  { key: "avanzadosVarones", label: "Avanzados Varones" },
  { key: "mixtas", label: "Mixtas" },
];

const MEDAL_COLUMNS = [
  { key: "gold", label: "Oro", dot: "bg-yellow-400" },
  { key: "silver", label: "Plata", dot: "bg-slate-400" },
  { key: "bronze", label: "Bronce", dot: "bg-orange-400" },
];

const thBase = "whitespace-nowrap px-2 py-2 text-[11px] font-bold uppercase tracking-wide";
const th = `${thBase} text-center text-slate-500`;
const thLeft = `${thBase} text-left text-slate-500`;
const thTotal = `${thBase} text-center text-indigo-700`;
const cellBase = "whitespace-nowrap px-2 py-2 text-center";
const cell = `${cellBase} text-sm text-slate-700`;

const frozen = buildFrozenColumns([
  ["rank", 3.5],
  ["institution", 12],
]);

const valueOrDash = (value) => (value === null || value === undefined ? "—" : value);

const StandingsView = ({ idevent, idsport }) => {
  const query = useAthleticsStandings({ idevent, idsport });
  const [downloading, setDownloading] = useState(false);
  const [pruebasOpen, setPruebasOpen] = useState(false);
  const downloadComputo = async () => {
    setDownloading(true);
    try {
      const file = await downloadAthleticsComputo({ idevent, idsport });
      saveAthleticsFile(file);
      toast.success(`Descargado: ${file.filename}`);
    } catch (error) {
      toast.error(parseAthleticsError(error).message, { duration: 9000 });
    } finally {
      setDownloading(false);
    }
  };
  const data = query.data || null;
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const placesCount = Math.max(8, ...rows.map((row) => (Array.isArray(row.places) ? row.places.length : 0)));
  const placeColumns = Array.from({ length: placesCount }, (_, index) => index);
  const pruebasContempladas = Array.isArray(data?.pruebasContempladas) ? data.pruebasContempladas : [];

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 sm:px-5">
        <div>
          <h3 className="flex items-center gap-1.5 text-lg font-bold text-indigo-700">
            Clasificación por universidad
            {pruebasContempladas.length ? (
              <button
                type="button"
                onClick={() => setPruebasOpen(true)}
                aria-label="Ver las pruebas que suman en la clasificación"
                title="Ver las pruebas que suman en la clasificación"
                aria-haspopup="dialog"
                className="inline-flex h-6 w-6 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
              >
                <FiInfo aria-hidden="true" />
              </button>
            ) : null}
          </h3>
          {data ? (
            <p className="mt-0.5 text-xs font-semibold text-slate-500">
              Pruebas oficiales: {valueOrDash(data.officialPruebas)} de {valueOrDash(data.totalPruebas)}
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          {data?.includesProvisional ? (
            <Badge tone="amber" title="Incluye pruebas aún no oficiales">
              Incluye provisionales
            </Badge>
          ) : null}
          <button
            type="button"
            onClick={downloadComputo}
            disabled={downloading || query.isLoading}
            title="PDF con el resumen, el detalle de cada total por institución y el anexo de combinadas"
            className={secondaryButtonClass}
          >
            {downloading ? <Spinner /> : <FaFilePdf aria-hidden="true" />} Descargar cómputo (PDF)
          </button>
          <button
            type="button"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
            className={secondaryButtonClass}
          >
            {query.isFetching ? <Spinner /> : <FiRefreshCw aria-hidden="true" />} Actualizar
          </button>
        </div>
      </div>

      {query.isLoading ? (
        <LoadingState label="Cargando clasificación..." />
      ) : query.isError ? (
        <ErrorState message={parseAthleticsError(query.error).message} onRetry={() => query.refetch()} />
      ) : !rows.length ? (
        <EmptyState
          title="Sin clasificación"
          description="Aún no hay pruebas con resultados que sumen a la clasificación por universidad."
        />
      ) : (
        <FrozenScroll>
          <table className="w-full min-w-[1100px] table-auto">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th {...frozen("rank", th)}>#</th>
                <th {...frozen("institution", thLeft)}>Universidad</th>
                {POINT_COLUMNS.map((column) => (
                  <th key={column.key} className={th} title={column.title}>
                    {column.label}
                  </th>
                ))}
                <th className={thTotal}>Total</th>
                {MEDAL_COLUMNS.map((column) => (
                  <th key={column.key} className={th} title={column.label}>
                    <span className="inline-flex items-center gap-1">
                      <span className={`h-2.5 w-2.5 rounded-full ${column.dot}`} aria-hidden="true" />
                      {column.label}
                    </span>
                  </th>
                ))}
                <th className={th}>Medallas</th>
                {placeColumns.map((index) => (
                  <th key={index} className={th}>
                    {index + 1}º
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={row.institution?.id ?? index} className="border-b border-slate-100 bg-white last:border-b-0 hover:bg-slate-50">
                  <td {...frozen("rank", `${cellBase} text-sm font-black text-slate-900`)}>
                    {row.rank === null || row.rank === undefined ? "–" : row.rank}
                    {row.tied ? <span className="ml-0.5 text-xs text-slate-400">=</span> : null}
                  </td>
                  <td {...frozen("institution", "px-2 py-2 text-left")}>
                    <span className="block text-sm font-bold text-slate-800">{row.institution?.abrev || "—"}</span>
                    <span className="block max-w-[11rem] truncate text-[11px] text-slate-500" title={row.institution?.name}>
                      {row.institution?.name}
                    </span>
                  </td>
                  {POINT_COLUMNS.map((column) => (
                    <td key={column.key} className={cell}>
                      {valueOrDash(row.points?.[column.key])}
                    </td>
                  ))}
                  <td className={`${cellBase} text-base font-black text-indigo-700`}>{valueOrDash(row.points?.total)}</td>
                  {MEDAL_COLUMNS.map((column) => (
                    <td key={column.key} className={cell}>
                      {valueOrDash(row.medals?.[column.key])}
                    </td>
                  ))}
                  <td className={`${cellBase} text-sm font-bold text-slate-700`}>{valueOrDash(row.medals?.total)}</td>
                  {placeColumns.map((placeIndex) => (
                    <td key={placeIndex} className={`${cellBase} text-xs text-slate-500`}>
                      {valueOrDash(Array.isArray(row.places) ? row.places[placeIndex] : null)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </FrozenScroll>
      )}
      <PruebasQueSumanModal
        open={pruebasOpen}
        onClose={() => setPruebasOpen(false)}
        pruebas={pruebasContempladas}
        puntajePorPuesto={data?.puntajePorPuesto}
      />
    </Card>
  );
};

export default StandingsView;
